import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, styled } from 'dripsy';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniOtpInput } from '@/design-system/components/inputs';
import { Icon } from '@/design-system/components/icons/Icon';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ChangeATMPINStackParamList, ChangeATMPINStackRoutes } from './type';
import { OtpInputVariant } from '@/design-system/components/inputs/uniOtpInput';
import { Card } from '../../../../../../packages/utx-access-client/src/react';
import { PageHeaderModalNative } from '../page/PageHeaderModalNative';

export interface ChangeATMPINProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  account: string;
  tab?: number;
  record?: Card;
  func?: (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
}

export const ChangeATMPINNativeZeroScreen: React.FC<ChangeATMPINProps> = (props) => {
  const { onClose, onLayout, onData, account, tab, record, func, onReturnData } = props;
  const { t } = useTranslation('account-management');
  const { body, textMb, textTitle, checkOnView, dotsymbolView, textCheckOn, textDotsymbol, btnView, btn } = sxStyles;

  const [pin, setPin] = useState<string | undefined>(undefined);
  const [rePin, setRePin] = useState<string | undefined>(undefined);
  const [repeatedNumber, setRepeatedNumber] = useState<boolean>(false);
  const [bothMatch, setBothMatch] = useState<boolean>(false);

  const navigation = useNavigation<NativeStackNavigationProp<ChangeATMPINStackParamList>>();

  const changeAtmPin = (submit = true, param = {}) => {
    let pinFlag = pin;
    let rePinFlag = rePin;
    if (Object.keys(param).length > 0) {
      if (param.field == 'pin') {
        setPin(param.value);
        pinFlag = param.value;
      } else if (param.field == 'rePin') {
        setRePin(param.value);
        rePinFlag = param.value;
      }
    }

    setRepeatedNumber(false);
    setBothMatch(false);
    var reg_repeated = /(.)\1{2}/;
    var reg_consecutive = /(.)(0(?=1)|1(?=2)|2(?=3)|3(?=4)|4(?=5)|5(?=6)|6(?=7)|7(?=8)|8(?=9)){2}/;
    if (!pinFlag || pinFlag.length < 6) {
      return;
    } else if (reg_repeated.test(pinFlag) || reg_consecutive.test(pinFlag)) {
      return;
    } else {
      setRepeatedNumber(true);
      if (pinFlag != rePinFlag) {
        return;
      } else {
        setBothMatch(true);
        if (submit) {
          onData({ pin });
          navigation.navigate(ChangeATMPINStackRoutes.Confirm, { onLayout, data: { pin }, account, tab, record, func, onReturnData });
        }
      }
    }
  };

  const { theme } = useDripsyTheme();

  return (
    <View sx={{ height: '100%', backgroundColor: '$white' }}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={onClose}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account-management::card-details.text--change-atm-pin')}
            </TextTitle>
          </View>
        </View>
      </PageHeaderModalNative>
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account-management::change-atm-pin.text--create-new-pin')}
        </TextTitle>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::change-atm-pin.text--enter-new-pin')}
        </TextCaption>
        <View sx={textMb}>
          <UniOtpInput onValueChange={(value) => changeAtmPin(false, { field: 'pin', value: value })} variant={OtpInputVariant.Rounded} />
        </View>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::change-atm-pin.text--re-enter-new-pin')}
        </TextCaption>
        <UniOtpInput onValueChange={(value) => changeAtmPin(false, { field: 'rePin', value: value })} variant={OtpInputVariant.Rounded} />
        <View sx={checkOnView}>
          <Icon name={repeatedNumber ? 'checkOn' : 'dotsymbol'} />
          <TextCaption sx={{ ...textCheckOn, color: repeatedNumber ? '$success' : '$textSecondary' }}>
            {t('card-account-management::change-atm-pin.text--no-consecutive-repeated-number')}
          </TextCaption>
        </View>
        <View sx={dotsymbolView}>
          <Icon name={bothMatch ? 'checkOn' : 'dotsymbol'} />
          <TextCaption sx={{ ...textDotsymbol, color: bothMatch ? '$success' : '$textSecondary' }}>
            {t('card-account-management::change-atm-pin.text--both-pin-match')}
          </TextCaption>
        </View>
        <View sx={btnView}>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::change-atm-pin.button--update')}
            onPress={changeAtmPin}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4
  },
  checkOnView: {
    marginTop: 13,
    flexDirection: 'row',
    alignItems: 'center'
  },
  dotsymbolView: {
    marginTop: 2,
    flexDirection: 'row',
    alignItems: 'center'
  },
  textCheckOn: {
    marginLeft: 10,
    fontWeight: 'body',
    color: '$success'
  },
  textDotsymbol: {
    marginLeft: 12,
    fontWeight: 'body',
    color: '$textSecondary'
  },
  btnView: {
    width: '92%',
    position: 'fixed',
    left: 0,
    bottom: 0,
    marginX: 15,
    marginBottom: 30
  },
  btn: {
    width: '100%',
    height: 44
  }
};
